import AdultSymptomExercisePage from '@/components/AdultSymptomExercisePage';
import { useLocaleStore } from '@/lib/stores/localeStore';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Overlay } from '@/components/ui/overlay';
import { ClientInformationForm } from '@/components/ClientInformationForm';
import ExerciseImage from '@/assets/exercise-background-photos/suicide-ideation.png';
import suicidePart1Mp4 from '@/assets/videos/adults-symptoms/suicide/suicide-part-1.mp4';
import suicidePart1Webm from '@/assets/videos/adults-symptoms/suicide/suicide-part-1.webm';
import suicidePart2Mp4 from '@/assets/videos/adults-symptoms/suicide/suicide-part-2.mp4';
import suicidePart2Webm from '@/assets/videos/adults-symptoms/suicide/suicide-part-2.webm';
import suicidePart3Mp4 from '@/assets/videos/adults-symptoms/suicide/suicide-part-3.mp4';
import suicidePart3Webm from '@/assets/videos/adults-symptoms/suicide/suicide-part-3.webm';
import suicidePart1Ar from '/subs/suicide-part-1.ar.vtt';
import suicidePart1En from '/subs/suicide-part-1.en.vtt';
import suicidePart2Ar from '/subs/suicide-part-2.ar.vtt';
import suicidePart2En from '/subs/suicide-part-2.en.vtt';
import suicidePart3Ar from '/subs/suicide-part-3.ar.vtt';
import suicidePart3En from '/subs/suicide-part-3.en.vtt';
import { isAdultExerciseGroup } from '@/lib/data/contentCatalog';

export const Route = createFileRoute('/(content)/adults/exercises/suicide-ideation/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    group: isAdultExerciseGroup(search.group) ? search.group : undefined,
    mode:
      search.mode === 'videos' || search.mode === 'exercises'
        ? (search.mode as 'videos' | 'exercises')
        : undefined,
  }),
  head: () => ({
    meta: [{ title: 'Suicidal Thoughts - HAMD' }],
  }),
  beforeLoad: () => ({
    title: 'exercises.categories.suicide-ideation',
    navbar: {
      transparent: true,
      showTitle: false,
    },
  }),
});

function RouteComponent() {
  const { group, mode } = Route.useSearch();
  const navigate = Route.useNavigate();
  const locale = useLocaleStore((state) => state.locale);
  const captionsDir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';
  const [isBookSessionOpen, setIsBookSessionOpen] = useState(false);

  const videos = [
    {
      sources: [{ src: suicidePart1Webm, type: 'video/webm' }, { src: suicidePart1Mp4, type: 'video/mp4' }],
            videoClassName: "w-full h-full object-contain bg-black/40",

      subtitleTracks: [
        { src: suicidePart1Ar, srcLang: 'ar', label: 'Arabic', default: true },
        { src: suicidePart1En, srcLang: 'en', label: 'English' },
      ],
      activeSubtitleLanguage: locale === 'ar' ? 'ar' : 'en',
      captionsDir,
      defaultCaptionsVisible: true,
      showCaptionsToggle: true,
    },
    {
      sources: [{ src: suicidePart2Webm, type: 'video/webm' }, { src: suicidePart2Mp4, type: 'video/mp4' }],
            videoClassName: "w-full h-full object-contain bg-black/40",

      subtitleTracks: [
        { src: suicidePart2Ar, srcLang: 'ar', label: 'Arabic', default: true },
        { src: suicidePart2En, srcLang: 'en', label: 'English' },
      ],
      activeSubtitleLanguage: locale === 'ar' ? 'ar' : 'en',
      captionsDir,
      defaultCaptionsVisible: true,
      showCaptionsToggle: true,
    },
    {
      sources: [{ src: suicidePart3Webm, type: 'video/webm' }, { src: suicidePart3Mp4, type: 'video/mp4' }],
            videoClassName: "w-full h-full object-contain bg-black/40",

      subtitleTracks: [
        { src: suicidePart3Ar, srcLang: 'ar', label: 'Arabic', default: true },
        { src: suicidePart3En, srcLang: 'en', label: 'English' },
      ],
      activeSubtitleLanguage: locale === 'ar' ? 'ar' : 'en',
      captionsDir,
      defaultCaptionsVisible: true,
      showCaptionsToggle: true,
    },
  ];

  return (
    <>
      <AdultSymptomExercisePage
        symptom="suicide-ideation"
        titleKey="exercises.categories.suicide-ideation"
        heroImage={ExerciseImage}
        videos={videos}
        mode={mode}
        onModeChange={(nextMode) =>
          navigate({
            to: Route.to,
            search: {
              mode: nextMode ?? undefined,
              group: nextMode === 'exercises' ? group : undefined,
            },
          })
        }
        selectedGroup={group}
        onSelectGroup={(nextGroup) =>
          navigate({
            to: Route.to,
            search: { mode: 'exercises', group: nextGroup },
          })
        }
        onBookSession={() => setIsBookSessionOpen(true)}
        isPaused={isBookSessionOpen}
      />
      <Overlay show={isBookSessionOpen} onClose={() => setIsBookSessionOpen(false)}>
        <ClientInformationForm onClose={() => setIsBookSessionOpen(false)} />
      </Overlay>
    </>
  );
}
